/**
 * MEDEVAC Module - PSP/CRE Tool v3.0
 *
 * Handles medical evacuation calculations including:
 * - Helicopter MEDEVAC capacity
 * - Ground ambulance capacity
 * - Blue Light Protocol
 * - Evacuation paradigm effects
 *
 * REPLACES ALL HARDCODED VALUES from v3.1:
 * - Line 806: 0.85 availability → AMBULANCE_AVAILABILITY constants
 * - Line 809: 3 trips × 2 patients → calculateFleetCapacity()
 *
 * @version 5.12.0
 * @date April 8, 2026
 *
 * VERSION 5.12.0 — ARCH-015: Remove ENH-038 double terrain application (April 8, 2026)
 *   ENH-038 added _terrainTimeFactor from evacuationRoutes.js ON TOP of terrainMod.speed_modifier
 *   from terrainFactors.js — terrain was already applied via effectiveSpeed = spec.speed_km_h x
 *   terrainMod.speed_modifier. The additional time multiplier double-counted terrain, inflating
 *   ground ambulance travel time (MOUNTAIN: 2.24x base instead of correct 1.49x). This produced
 *   the 9-min golden hour spread in ARCH-015. Fix: remove _terrainTimeFactor and the
 *   evacuationRoutes.js import. Terrain is correctly applied once via effectiveSpeed.
 *   Impact: ground ambulance travel times reduced to physically accurate values. GH compliance
 *   badge will now correctly reflect achievable cases that were previously over-penalised.
 *
 * VERSION 5.11.0 — ENH-048a: Monte Carlo medevac_reliability wired (March 28, 2026)
 *   Reads scenario.operation_parameters.monte_carlo_modifiers.medevac_reliability (±30%).
 *   Applied as post-processing scale to helicopterCapacity and groundCapacity immediately
 *   after calculateHelicopterCapacity() + calculateGroundAmbulanceCapacity() calls.
 *   Higher value = more reliable MEDEVAC = more patient throughput (medevac_reliability=1.3
 *   → +30% capacity). Lower = fleet degraded (0.7 → −30%). No-op at 1.0 (deterministic runs).
 *   Scales: available_aircraft, daily_sorties, daily_patient_capacity (air);
 *           available_vehicles, total_daily_capacity (ground).
 *
 * VERSION 5.10.0 — ENH-038: evacuationRoutes.js terrain time factor wired into ground ambulance (March 27, 2026)
 *   Import: TERRAIN_FACTORS from evacuationRoutes.js.
 *   calculateGroundAmbulanceCapacity: _terrainTimeFactor = TERRAIN_FACTORS[terrain]?.factor (1.0–2.0).
 *   Replaces inline (distanceKm × 2) / effectiveSpeed × 60 formula with the import-backed version.
 *   Terrain time factors now stacked on top of terrainMod.speed_modifier (speed reduction) and
 *   existing threat/weather/night modifiers. MOUNTAIN terrain: both speed −25% AND time ×1.5 applied.
 *   Fallback: unknown terrain → factor 1.0 (PLAINS baseline, no regression for existing scenarios).
 *
 * VERSION 5.9.0 — ENH-032: nightOperations.js wired into MEDEVAC capacity (March 27, 2026)
 *   calculateComprehensiveMEDEVAC: extracts nightOpsModifiers from
 *   scenario.operation_parameters.night_ops_config.modifiers (pre-computed by Tab2).
 *   Passes nightOpsModifiers to calculateHelicopterCapacity() and calculateGroundAmbulanceCapacity().
 *   In calculateHelicopterCapacity(): nightMod now uses nightOpsModifiers.medevac_modifier
 *     (NONE=1.0, LIMITED=0.95, MODERATE=0.85, HIGH=0.70, CONTINUOUS=0.75, ×illumination ×NVG)
 *     instead of hardcoded NIGHT_MEDEVAC_MODIFIERS.helicopter_availability (0.30).
 *   In calculateGroundAmbulanceCapacity(): nightMod now uses nightOpsModifiers.ground_evac_modifier
 *     instead of hardcoded NIGHT_MEDEVAC_MODIFIERS.ground_speed (0.50).
 *   Falls back to existing binary nightMod when nightOpsModifiers is null/disabled.
 *
 * VERSION 5.8.0 — ENH-002: getEffectiveAssets(facility, day) (March 15, 2026)
 *   NEW EXPORT: getEffectiveAssets(facility, day).
 *   Returns net helicopter + ambulance counts for a facility on a given operation day,
 *   applying attached_assets[] (adds) and detached_assets[] (subtracts).
 *   Active window logic: attach_day <= day <= (detach_day ?? Infinity) for attachments;
 *   detach_day <= day <= (return_day ?? Infinity) for detachments.
 *   Returns: { helicopters, ambulances, organic_*, delta_*, attachments_active[], detachments_active[] }.
 *   Backward compatible: facilities with no attached/detached arrays return organic counts unchanged.
 *
 * VERSION 5.7.0 — ENH-016: Friendly Air Situation MEDEVAC AD threat override (March 10, 2026)
 *   NEW IMPORT: AIR_SITUATION_LEVELS from airSituation.js.
 *   calculateComprehensiveMEDEVAC: reads scenario.operation_parameters.air_situation_config.level.
 *   Applies medevac_ad_threat_override from AIR_SITUATION_LEVELS before adThreat is passed to
 *   calculateHelicopterCapacity. AIR_SUPERIORITY→PERMISSIVE, CONTESTED→HIGH,
 *   ENEMY_AIR_SUPERIORITY→DENIED, PARITY→null (no override, Tab3 ad_threat used).
 *   adThreatRaw still available for diagnostics. adThreat = override ?? adThreatRaw.
 *   Backward compatible: missing air_situation_config defaults to PARITY (no override).
 *
 * VERSION 5.6.0 — IMP-007: compositeUnitFactors wired into helicopter + ground capacity (March 9, 2026)
 *   calculateComprehensiveMEDEVAC: accepts compositeUnitFactors = null (backward compat).
 *   calculateHelicopterCapacity: unitHelicopterFactor applied to dailyPatientCapacity.
 *   calculateGroundAmbulanceCapacity: unitWheeledFactor/unitTrackedFactor applied per
 *   vehicle category in fleet loop (ARMORED/TRACKED → tracked factor; WHEELED/BUS → wheeled).
 *   All three modifiers blocks updated with unit_type_* fields for Tab5 transparency.
 *
 * VERSION 5.5.0 — Phase 5.1: tactical_evac_config wired into calculateComprehensiveMEDEVAC (March 5, 2026)
 *   tactical_evac_config (Tab3 Section 3B.0) now takes precedence for ad_threat and weather params.
 *   Fallback to scenario.operation_parameters for backward compatibility with pre-Phase-5 scenarios.
 *   Weather key updated: default 'CLEAR' → 'VMC_CLEAR' (matches helicopterSpecs canonical keys).
 *
 * VERSION 5.4.0 — Phase F F-06: NTM-corrected Golden Hour + v2.3 key fixes (March 1, 2026)
 *
 * F-06 FIX (MAJOR): NTM incorporated into Golden Hour range ring calculation.
 *   Golden Hour clock starts at point of wounding, not at helicopter launch.
 *   NTM (Notice To Move — time from alert to wheels-up) consumes part of the 60-minute budget.
 *   Previous formula: heliEvacTime = distance × 2 + 15 (ignored NTM entirely).
 *   Corrected formula: heliEvacTime = NTM + distance × 2 + 15.
 *   NTM values from MEDEVAC_OPERATIONAL_FACTORS.ntm_minutes (IAR330=10, UH60=15, CH47=30).
 *   New output fields in golden_hour_analysis: ntm_minutes_used, gh_range_ring_km.
 *   gh_range_ring_km = max distance at which GH is still achievable = (60 − NTM − 15) / 2.
 *
 * F-04 FIX (MODERATE — resolved by helicopterSpecs.js v2.3, March 1, 2026):
 *   MEDEVAC_OPERATIONAL_FACTORS now contains all required sub-fields:
 *   sorties_per_aircraft_per_day, patients_per_sortie, weather_degradation, ad_threat_modifier,
 *   ntm_minutes. All v2.3 keys are UPPERCASE (PERMISSIVE/CONTESTED/DENIED for sorties;
 *   IAR330/UH60/etc for patients_per_sortie and ntm_minutes).
 *   calculateHelicopterCapacity() corrected: envKey/adKey/weatherKey now toUpperCase().
 *   adMod, weatherMod, baseSortiesPerAircraft now read from constant correctly.
 *   patientsPerSortie now reads by helicopter code (not dead .mixed_load key).
 *   Inline fallback object removed — constant is now complete (APP_RULES 6.1).
 *
 * VERSION 5.3.0 — Phase F (ARCH-007) Fix (February 27, 2026)
 *
 * F-01 FIX (CRITICAL): Wrong field name for Tab3 evacuation distance.
 *   Tab3 buildUpdates saves medical_configuration.mtf_distances.role2_distance_km.
 *   Module was reading mtf_distances?.role2 — that key does not exist in Tab3's schema.
 *   Result: always undefined → || 25 fallback always fired silently (APP_RULES 6.1 violation).
 *   Every calculation ran on 25 km regardless of user configuration in Tab3.
 *   Fix: read role2_distance_km and role1_distance_km. Throw APP_RULES 6.3 error if absent.
 *
 * F-02 (ROOT CAUSE OF 100% GOLDEN HOUR): Resolved by fixing F-01.
 *   At 25 km (contested terrain, combined modifier 0.68) ground evac = 54.1 min → GH achieved.
 *   Break-even distance is 28.3 km — the fallback kept ALL scenarios below this threshold.
 *   With correct user-configured distances (e.g. 40 km) GH degrades to 66%.
 *
 * F-03 FIX (MAJOR): Helicopter and ground ambulance use different distances.
 *   Helicopter: POI (at FEBA) → R2B = role2_distance_km (full distance from FEBA).
 *   Ground ambulance: R1 → R2B = role2_distance_km - role1_distance_km (the R1→R2B leg only).
 *   Previous code passed the same distanceKm to both — ground ambulance trip was overstated
 *   by role1_distance_km (8 km default) which is already covered by the R1 facility.
 *   Both values now carried as separate heliDistKm / groundDistKm in calculateComprehensiveMEDEVAC.
 *
 * F-04 NOTE (MODERATE — deferred to helicopterSpecs.js v2.3):
 *   MEDEVAC_OPERATIONAL_FACTORS is missing sorties_per_aircraft_per_day, patients_per_sortie,
 *   weather_degradation, and ad_threat_modifier fields. These fallback to the inline object
 *   at line 364, which has correct values but is not the constants-based source of truth.
 *   The environment-aware sortie calculation (permissive=8, contested=6, denied=3) correctly
 *   falls back to 6 (contested baseline), but PERMISSIVE and DENIED scenarios are wrong.
 *   This module cannot fix this without modifying helicopterSpecs.js. Tracked as F-04.
 *   In-module workaround added: explicit sorties/patients/ad fallback constants documented.
 *
 * F-05 NOTE (MINOR — deferred):
 *   medicalConfig.surgical_config?.backlog?.total is always 0.
 *   Tab3 buildUpdates does not populate surgical_config.backlog — it saves only
 *   {teams, shift_mode, fatigue_level}. The SURGICAL_BACKLOG_CRITICAL Blue Light
 *   trigger is permanently disabled until Tab3 (or the calculator) populates this field.
 *   No change in this fix (requires Tab3/calculator coordination).
 *
 * VERSION 5.2.1 - BUG FIX: Zero-helicopter doctrinal scenario (February 25, 2026)
 * - MED-08 CORRECTION: calculateHelicopterCapacity() was throwing when totalAircraft === 0.
 *   Zero organic helicopters is a VALID doctrinal state for Romanian/NATO forces (Air Force
 *   assets, Division CAB, MOU-based). The throw crashed Tab3→Tab4 and Tab4→Tab5 navigation
 *   and caused a blank Tab5 screen. Fix: return a zero-capacity result with the same shape
 *   as the normal return. All callers already guard on available_helicopters > 0 correctly.
 * VERSION 5.2.0 - MED-01/02/04/05/06/07 constants migration (February 24, 2026) resolveWeatherModifier() added with
 *   key adapter (MARGINAL→RAIN_LIGHT, POOR→RAIN_HEAVY, SEVERE→SNOW_HEAVY) and field
 *   adapter (.ground_speed_modifier→.ground, .helicopter_availability→.air). Reads
 *   WEATHER_EVACUATION_MODIFIERS from evacuationParadigms.js.
 * - MED-02/04: Local TERRAIN_GROUND_MODIFIERS deleted. resolveTerrainModifier() added
 *   with key adapter (OPEN→PLAINS, MARSH→SWAMP_WETLAND, RIVER→SWAMP_WETLAND,
 *   MIXED→ROLLING_HILLS). Reads TERRAIN_TYPES from terrainFactors.js v1.1.0
 *   (.speed_modifier, .availability_modifier alias fields added in v1.1.0).
 * - MED-05: Local THREAT_GROUND_MODIFIERS deleted. resolveThreatModifier() added with
 *   key adapter (LOW→LOW_THREAT, MEDIUM→MODERATE_THREAT, INTENSE/PEER→EXTREME_THREAT).
 *   Synthesizes .speed_modifier + .risk_modifier from THREAT_SPEED_FROM_PARADIGM lookup.
 * - MED-07: Local NIGHT_MEDEVAC_MODIFIERS deleted. Rebuilt as computed const using
 *   NIGHT_OPERATIONS_MODIFIERS from evacuationParadigms.js (NON_EQUIPPED helicopter,
 *   BLACKOUT_DRIVE ground as planning defaults).
 * VERSION 5.1.0 - MED-11 fix (February 24, 2026)
 * - MED-11: `distanceKm * 2 + 15` replaced with MEDEVAC_OPERATIONAL_FACTORS.evac_planning_mins_per_km
 *   and MEDEVAC_OPERATIONAL_FACTORS.evac_planning_load_minutes. Values unchanged (2 min/km, 15 min).
 *   Constants added to helicopterSpecs.js v2.2 MEDEVAC_OPERATIONAL_FACTORS block.
 *
 * VERSION 5.0 - Phase 4-A partial constants migration (February 22, 2026)
 * @author Dr. Tatar Radu MD
 *
 * VERSION 5.0 - Phase 4-A partial constants migration (February 22, 2026)
 * - MED-03: Added MASCAL_TRIGGERS import from mascalThresholds.js.
 * - MED-08: calculateHelicopterCapacity() — removed `totalAircraft = 4` silent default.
 *   Missing pool configuration now throws with context (APP_RULES 6.3).
 * - MED-09: calculateBlueLightConvoy() — replaced 4 inline numeric literals with
 *   BLUE_LIGHT_PROTOCOL.convoy_ratios fields (litter_patient_percent, patients_per_vehicle,
 *   ambulatory_patients_per_vehicle, escort_vehicle_ratio). EXT-02 delivered these.
 * - MED-10: evaluateBlueLightProtocol() — replaced `surgicalBacklogThreshold = 24`
 *   with MASCAL_TRIGGERS.SURGICAL_BACKLOG.role_2_basic.mascal_critical (= 25).
 *
 * DEFERRED items:
 * - MED-11: ✅ DONE in v5.1.0 — MEDEVAC_OPERATIONAL_FACTORS literals replaced.
 * - MED-01/06: ✅ DONE in v5.2.0 — see VERSION 5.2.0.
 * - MED-02/04: ✅ DONE in v5.2.0 — see VERSION 5.2.0.
 * - MED-05: ✅ DONE in v5.2.0 — see VERSION 5.2.0.
 * - MED-07: ✅ DONE in v5.2.0 — see VERSION 5.2.0.
 */

// ============================================================================
// IMPORTS
// ============================================================================

// Ambulance types - using adapter for calculation-friendly format
import { resolveInput } from '../assumptionLedger.js';
import { groundCycleMinutes, groundRunsPerDay, groundBaseSpeedKph, vehicleClassFor } from './evacuationCapacityModel.js';
import {
  AMBULANCE_TYPES,
  getAmbulanceCalcSpec
} from '../../constants/ambulanceTypes.js';

// Helicopter specs - using operational factors and terrain modifiers
import {
  MEDEVAC_OPERATIONAL_FACTORS,
  TERRAIN_MODIFIERS
} from '../../constants/helicopterSpecs.js';

// MASCAL/Blue Light Protocol thresholds + MASCAL activation triggers
import {
  BLUE_LIGHT_PROTOCOL,
  MASCAL_TRIGGERS       // MED-03: Surgical backlog threshold (surgicalBacklogThreshold)
} from '../../constants/mascalThresholds.js';

// MED-01/06/07: Weather + Night modifiers (replaces local WEATHER_MODIFIERS + NIGHT_MEDEVAC_MODIFIERS)
import {
  WEATHER_EVACUATION_MODIFIERS,  // MED-01/06: .helicopter_availability, .ground_speed_modifier
  NIGHT_OPERATIONS_MODIFIERS,    // MED-07: .HELICOPTER.*, .GROUND.*
  resolveEvacuationParadigm      // CALC-005: single reader of operation_parameters.evacuation_paradigm
} from '../../constants/evacuationParadigms.js';

// CALC-011: planner's terrain/weather (Tab 2) via one resolver
import { resolveScenarioEnvironment } from '../scenarioEnvironment.js';
import { THREAT_EFFECTS_USE, helicopterAvailability, infrastructureModel } from '../../constants/threatEffects.js';

// MED-02/04: Terrain modifiers (replaces local TERRAIN_GROUND_MODIFIERS)
import {
  TERRAIN_TYPES as TERRAIN_TYPES_TF  // MED-02/04: .speed_modifier, .availability_modifier (v1.1.0)
} from '../../constants/terrainFactors.js';

// MED-05: Threat levels for ground evac speed (replaces local THREAT_GROUND_MODIFIERS)
import {
  THREAT_LEVELS
} from '../../constants/operationsConfig.js';

// ENH-016: Air situation AD threat override for MEDEVAC planning
import {
  AIR_SITUATION_LEVELS
} from '../../constants/airSituation.js';

// ARCH-015: ENH-038 TERRAIN_FACTORS import from evacuationRoutes.js removed.
// Terrain is fully accounted for via terrainMod.speed_modifier (from terrainFactors.js, imported
// above as TERRAIN_TYPES_TF). The ENH-038 _terrainTimeFactor stacked a second terrain multiplier
// on top of the speed reduction, double-counting terrain difficulty. See VERSION 5.12.0 above.

// ============================================================================
// CONSTANTS - From research, NO HARDCODING
// ============================================================================

/**
 * Ambulance type specifications
 * If not in constants, define with proper research values
 */
const AMBULANCE_TYPE_SPECS = AMBULANCE_TYPES || {
  M113_ARMORED: {
    label: 'M113 Armored Ambulance',
    category: 'ARMORED',
    litter_capacity: 4,
    ambulatory_capacity: 2,
    typical_availability: 0.70,      // Lower - tracked vehicle
    trips_per_day: 2.5,              // Slower
    speed_km_h: 40,
    terrain_capability: 'ALL',
    protection_level: 'MEDIUM',
    crew: 3,
    load_time_minutes: 8,
    unload_time_minutes: 5
  },
  HMMWV_AMBULANCE: {
    label: 'HMMWV Ambulance',
    category: 'WHEELED',
    litter_capacity: 4,
    ambulatory_capacity: 0,
    typical_availability: 0.85,
    trips_per_day: 4,
    speed_km_h: 80,
    terrain_capability: 'ROAD_IMPROVED',
    protection_level: 'LOW',
    crew: 2,
    load_time_minutes: 5,
    unload_time_minutes: 3
  },
  TABC_79: {
    label: 'TABC-79 (Romanian)',
    category: 'ARMORED',
    litter_capacity: 4,
    ambulatory_capacity: 4,
    typical_availability: 0.75,
    trips_per_day: 3,
    speed_km_h: 60,
    terrain_capability: 'ALL',
    protection_level: 'MEDIUM',
    crew: 3,
    load_time_minutes: 6,
    unload_time_minutes: 4
  },
  PIRANHA_MEDEVAC: {
    label: 'Piranha MEDEVAC',
    category: 'ARMORED',
    litter_capacity: 6,
    ambulatory_capacity: 4,
    typical_availability: 0.75,
    trips_per_day: 3,
    speed_km_h: 70,
    terrain_capability: 'ALL',
    protection_level: 'HIGH',
    crew: 3,
    load_time_minutes: 7,
    unload_time_minutes: 4
  },
  MEDICAL_BUS: {
    label: 'Medical Bus (T3 Only)',
    category: 'BUS',
    litter_capacity: 0,              // Bus cannot carry litters typically
    ambulatory_capacity: 30,
    typical_availability: 0.90,
    trips_per_day: 2,
    speed_km_h: 60,
    terrain_capability: 'ROAD_ONLY',
    protection_level: 'NONE',
    crew: 2,
    load_time_minutes: 15,
    unload_time_minutes: 10,
    t3_only: true
  },
  FIELD_AMBULANCE: {
    label: 'Field Ambulance (4x4)',
    category: 'WHEELED',
    litter_capacity: 2,
    ambulatory_capacity: 4,
    typical_availability: 0.85,
    trips_per_day: 5,
    speed_km_h: 80,
    terrain_capability: 'IMPROVED',
    protection_level: 'LOW',
    crew: 2,
    load_time_minutes: 4,
    unload_time_minutes: 3
  }
};

/**
 * Terrain modifiers for ground evacuation
 * MED-04 DEFERRED: Replace with TERRAIN_TYPES from terrainFactors.js (.ground_modifier).
 * terrainFactors.js does not exist yet. Key mapping also needs verification:
 * local keys (URBAN/MOUNTAIN/etc.) vs terrainFactors keys.
 */
// MED-02/04 FIX (February 24, 2026): Local TERRAIN_GROUND_MODIFIERS deleted.
// TERRAIN_TYPES_TF imported from terrainFactors.js v1.1.0.
// Key adapter below maps caller keys (OPEN, MARSH, RIVER, MIXED) to canonical keys.
// Field adapter: canonical .speed_modifier, .availability_modifier (added in v1.1.0).
const TERRAIN_KEY_MAP = {
  OPEN:     'PLAINS',         // Open terrain → plains (nearest equivalent)
  MARSH:    'SWAMP_WETLAND',  // Marsh → swamp/wetland
  RIVER:    'SWAMP_WETLAND',  // River crossing → wetland restrictions
  MIXED:    'ROLLING_HILLS',  // Mixed → moderate terrain baseline
  // Pass-through keys (same in both):
  URBAN:    'URBAN',
  MOUNTAIN: 'MOUNTAIN',
  FOREST:   'FOREST',
  PLAINS:   'PLAINS',
  ROLLING_HILLS: 'ROLLING_HILLS',
  DESERT:   'DESERT',
  COASTAL:  'COASTAL',
  ARCTIC:   'ARCTIC',
  SWAMP_WETLAND: 'SWAMP_WETLAND'
};
// Fallback values if canonical entry somehow missing (defensive programming, should never fire)
const TERRAIN_FALLBACK = { speed_modifier: 0.80, availability_modifier: 0.90 };
/**
 * Resolve terrain data from canonical terrainFactors.js TERRAIN_TYPES.
 * Returns an object with .speed_modifier and .availability_modifier.
 * @param {string} terrainKey - Caller-supplied key (local or canonical)
 */
function resolveTerrainModifier(terrainKey) {
  const canonicalKey = TERRAIN_KEY_MAP[terrainKey?.toUpperCase()] || terrainKey?.toUpperCase();
  return TERRAIN_TYPES_TF[canonicalKey] || TERRAIN_FALLBACK;
}

/**
 * Threat level modifiers for ground evacuation
 * MED-05 DEFERRED: Replace with THREAT_LEVELS from operationsConfig.js.
 * Key mismatch: local uses LOW/MEDIUM/HIGH/INTENSE/PEER; canonical uses
 * LOW_THREAT/MODERATE_THREAT/HIGH_THREAT/etc. Also: canonical THREAT_LEVELS
 * has evacuation_speed_modifier but no risk_modifier field. Requires
 * calculateGroundAmbulanceCapacity() refactor to use new key names.
 */
// MED-05 FIX (February 24, 2026): Local THREAT_GROUND_MODIFIERS deleted.
// THREAT_LEVELS imported from operationsConfig.js. Key adapter below maps
// local keys to canonical keys. operationsConfig THREAT_LEVELS does not have
// .risk_modifier; it does have .medical.golden_hour_probability (which replaces
// the conceptual purpose of risk_modifier). All read sites in this module only
// use .speed_modifier — so we read the paradigm-equivalent speed from medical sub-key.
// Building adapter that synthesizes speed_modifier + risk_modifier from canonical fields:
const THREAT_KEY_MAP = {
  LOW:      'LOW_THREAT',
  MEDIUM:   'MODERATE_THREAT',
  HIGH:     'HIGH_THREAT',
  INTENSE:  'EXTREME_THREAT',
  PEER:     'EXTREME_THREAT',
  // Pass-through canonical keys:
  LOW_THREAT:      'LOW_THREAT',
  MODERATE_THREAT: 'MODERATE_THREAT',
  HIGH_THREAT:     'HIGH_THREAT',
  EXTREME_THREAT:  'EXTREME_THREAT'
};
// Derived evacuation speed from golden_hour_probability (proxy for threat intensity)
const THREAT_SPEED_FROM_PARADIGM = {
  LOW_THREAT:      { speed_modifier: 1.00, risk_modifier: 1.00 },
  MODERATE_THREAT: { speed_modifier: 0.85, risk_modifier: 1.15 },
  HIGH_THREAT:     { speed_modifier: 0.70, risk_modifier: 1.35 },
  EXTREME_THREAT:  { speed_modifier: 0.55, risk_modifier: 1.55 }
};
const THREAT_FALLBACK = { speed_modifier: 0.85, risk_modifier: 1.15 };
/**
 * Resolve threat modifier object from canonical THREAT_LEVELS.
 * Returns object with .speed_modifier and .risk_modifier.
 * @param {string} threatKey - Local or canonical threat key
 */
function resolveThreatModifier(threatKey) {
  const canonicalKey = THREAT_KEY_MAP[threatKey?.toUpperCase()] || 'MODERATE_THREAT';
  return THREAT_SPEED_FROM_PARADIGM[canonicalKey] || THREAT_FALLBACK;
}

/**
 * Weather modifiers
 * MED-06 DEFERRED: Replace with WEATHER_EVACUATION_MODIFIERS from evacuationParadigms.js
 * (keys: CLEAR/RAIN_LIGHT/RAIN_HEAVY/SNOW/FOG; fields: .helicopter_availability,
 * .ground_speed_modifier). evacuationParadigms.js does not exist yet.
 */
// MED-06 FIX (February 24, 2026): Local WEATHER_MODIFIERS deleted.
// WEATHER_EVACUATION_MODIFIERS imported from evacuationParadigms.js.
// Key adapter maps local legacy keys to canonical keys.
// Field adapter: canonical .ground_speed_modifier → .ground; .helicopter_availability → .air
const WEATHER_KEY_MAP = {
  CLEAR:         'CLEAR',
  PARTLY_CLOUDY: 'PARTLY_CLOUDY',
  OVERCAST:      'OVERCAST',
  MARGINAL:      'RAIN_LIGHT',   // Legacy MARGINAL ≈ light rain (ground 0.85, air 0.75)
  POOR:          'RAIN_HEAVY',   // Legacy POOR ≈ heavy rain (ground 0.65, air 0.40)
  SEVERE:        'SNOW_HEAVY',   // Legacy SEVERE ≈ heavy snow/blizzard (ground 0.35, air 0.15)
  RAIN_LIGHT:    'RAIN_LIGHT',
  RAIN_HEAVY:    'RAIN_HEAVY',
  FOG:           'FOG',
  SNOW_LIGHT:    'SNOW_LIGHT',
  SNOW_HEAVY:    'SNOW_HEAVY',
  DUST_STORM:    'DUST_STORM'
};
const WEATHER_FALLBACK = { ground: 1.0, air: 1.0 };
/**
 * Resolve weather modifier from canonical WEATHER_EVACUATION_MODIFIERS.
 * Returns object with .ground (ground_speed_modifier) and .air (helicopter_availability).
 * @param {string} weatherKey - Local or canonical weather key
 */
function resolveWeatherModifier(weatherKey) {
  const canonicalKey = WEATHER_KEY_MAP[weatherKey?.toUpperCase()] || weatherKey?.toUpperCase();
  const entry = WEATHER_EVACUATION_MODIFIERS[canonicalKey];
  if (!entry) return WEATHER_FALLBACK;
  return {
    ground: entry.ground_speed_modifier,
    air: entry.helicopter_availability
  };
}

/**
 * Night operations modifiers
 * MED-07 DEFERRED: Replace with NIGHT_OPERATIONS_MODIFIERS from evacuationParadigms.js.
 * evacuationParadigms.js does not exist yet.
 */
// MED-07 FIX (February 24, 2026): Local NIGHT_MEDEVAC_MODIFIERS deleted.
// NIGHT_OPERATIONS_MODIFIERS imported from evacuationParadigms.js.
// Canonical is structured by HELICOPTER/GROUND sub-keys and NVG equipment level.
// Local values were: helicopter_availability=0.5 (≈ non-equipped), ground_speed=0.7 (≈ NVG).
// This module uses NON_EQUIPPED (worst-case planning) for availability and
// BLACKOUT_DRIVE (standard default) for ground speed.
// If caller can specify NVG equipment level, expand resolveNightModifier() below.
const NIGHT_MEDEVAC_MODIFIERS = {
  // Synthesized flat modifiers from NIGHT_OPERATIONS_MODIFIERS for backward compatibility
  ground_speed:           NIGHT_OPERATIONS_MODIFIERS.GROUND.BLACKOUT_DRIVE.speed_modifier,          // 0.50
  helicopter_availability: NIGHT_OPERATIONS_MODIFIERS.HELICOPTER.NON_EQUIPPED.availability_modifier, // 0.30
  accident_risk:          1 + NIGHT_OPERATIONS_MODIFIERS.GROUND.BLACKOUT_DRIVE.accident_risk_increase, // 1.25
  nvg_ground_recovery:    NIGHT_OPERATIONS_MODIFIERS.GROUND.NVG_EQUIPPED.speed_modifier,            // 0.75
  nvg_air_recovery:       NIGHT_OPERATIONS_MODIFIERS.HELICOPTER.NVG_EQUIPPED.availability_modifier  // 0.85
};

// ============================================================================
// HELICOPTER MEDEVAC FUNCTIONS
// ============================================================================

/**
 * Calculate helicopter MEDEVAC capacity
 * 
 * Uses constants from helicopterSpecs.js and medevacPools.js
 * NO HARDCODED VALUES
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Helicopter capacity analysis
 */
export function calculateHelicopterCapacity(config) {
  const {
    medevacConfig = {},
    environment = 'CONTESTED',
    adThreat = 'MEDIUM',
    weather = 'CLEAR',
    isNight = false,
    terrain = 'MIXED',
    unitHelicopterFactor = 1.0,  // IMP-007: unit-type helicopter demand scalar
    nightOpsModifiers = null,    // ENH-032: pre-computed from nightOperations.js via Tab2
    hlzConfig = null,            // V5-014: HLZ capability object from calculateComprehensiveMEDEVAC
    airModel = null              // 2026-10-13 (Tab 2 plan 7.9): threatEffects.helicopterAvailability(): air situation x enemy AD x weather
  } = config;

  // Get operational factors from constants
  // F-04 RESOLVED (helicopterSpecs.js v2.3, March 1, 2026):
  // All sub-fields are now present in MEDEVAC_OPERATIONAL_FACTORS.
  // Keys are UPPERCASE — sorties/ad/weather lookups use toUpperCase() below.
  // Inline fallback object removed per APP_RULES 6.1 (no fallback masking).
  const opFactors = MEDEVAC_OPERATIONAL_FACTORS;

  // Count total helicopters from pools
  let totalAircraft = 0;
  const poolBreakdown = {};

  if (medevacConfig.pools) {
    for (const [poolName, pool] of Object.entries(medevacConfig.pools)) {
      if (pool.enabled) {
        const count = pool.aircraft_count || 0;
        totalAircraft += count;
        poolBreakdown[poolName] = {
          count,
          type: pool.aircraft_type || 'GENERIC',
          enabled: true
        };
      }
    }
  }

  // MED-08 (v5.2.1 CORRECTION): Zero organic helicopters is a valid doctrinal state.
  // Romanian forces: Air Force assets only — no brigade-organic MEDEVAC (APP_RULES 8.2).
  // US forces: Division CAB provides MEDEVAC, not brigade-organic.
  // Return zero-capacity result with same shape as normal return so all callers handle
  // this correctly. Callers already guard on available_helicopters > 0.
  // Throwing here was wrong: it crashed Tab3→Tab4 and Tab4→Tab5 for any force without
  // organic helicopter pools, and caused the blank Tab5 screen.
  if (totalAircraft === 0) {
    return {
      total_aircraft: 0,
      available_aircraft: 0,
      effective_availability_percent: 0,
      daily_sorties: 0,
      daily_patient_capacity: 0,
      pools: poolBreakdown,
      modifiers: {
        maintenance: 0,
        crew: 0,
        ad_threat: 0,
        weather: 0,
        night: 0,
        terrain: 0,
        combined: 0,
        unit_type_helicopter: unitHelicopterFactor  // IMP-007
      },
      operational_factors: {
        sorties_per_aircraft: 0,
        patients_per_sortie: 0,
        environment: environment
      },
      limiting_factors: [
        'No organic MEDEVAC helicopters configured. Ground evacuation is primary method.',
        'Attach Air Force/Division CAB assets via ENH-002 for helicopter capability.'
      ],
      is_degraded: true,
      is_denied: false,
      no_organic_helicopters: true,
      educational_note:
        'NO ORGANIC MEDEVAC: This force has no assigned helicopter assets. ' +
        'Romanian Army doctrine: Air Force provides MEDEVAC under MOU — not brigade-organic. ' +
        'Ground evacuation (Blue Light Protocol) is the primary casualty movement method. ' +
        'Attach aviation assets via Attach/Detach (ENH-002) for helicopter capability.'
    };
  }

  // Calculate availability modifiers
  const maintenanceAvail = opFactors.maintenance_availability;
  const crewAvail = opFactors.crew_availability || 0.90;
  
  // F-04 FIX: v2.3 keys are UPPERCASE — must match exactly.
  // Previous code used .toLowerCase() → all lookups returned undefined.
  const envKey     = environment.toUpperCase(); // 'PERMISSIVE' | 'CONTESTED' | 'DENIED'
  const adKey      = adThreat.toUpperCase();    // 'PERMISSIVE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'DENIED'
  const weatherKey = weather.toUpperCase();     // 'VMC_CLEAR' | 'VMC_MARGINAL' | 'IMC_LIGHT' | etc.

  // AD threat modifier — reads flat value from ad_threat_modifier (v2.3 added this field)
  // 2026-10-13 (Tab 2 plan 7.9): ONE helicopter availability. With the Tab 2 threat assessment the air situation ceiling x the enemy air defense
  // replace the Tab 3 AD threat code and the air situation override (four overlapping settings before).
  const adMod = airModel
    ? airModel.parts.air_situation * airModel.parts.enemy_air_defense * (airModel.parts.coordination ?? 1)
    : (opFactors.ad_threat_modifier?.[adKey] ??
      (adThreat === 'LOW' ? 0.85 : adThreat === 'MEDIUM' ? 0.60 : adThreat === 'HIGH' ? 0.30 : 0.60));

  // Weather degradation — v2.3 structure: weather_degradation[KEY].sortie_modifier
  // Fallback: resolveWeatherModifier reads WEATHER_EVACUATION_MODIFIERS from evacuationParadigms.js
  const weatherDegEntry = opFactors.weather_degradation?.[weatherKey];
  const weatherMod = airModel
    ? airModel.parts.weather   // the operation weather, relative to clear (the Tab 3 tactical weather no longer counts a second time)
    : (weatherDegEntry?.sortie_modifier ?? resolveWeatherModifier(weather).air);

  // Night modifier
  // ENH-032: Use level-based nightOpsModifiers.medevac_modifier when available (Tab2 night ops config).
  // Encodes NIGHT_OPS_LEVELS × ILLUMINATION_CONDITIONS × NVG_AVAILABILITY (getNightOpsModifiers()).
  // Falls back to binary NIGHT_MEDEVAC_MODIFIERS.helicopter_availability (0.30) when absent.
  // 2026-10-13 (Tab 2 plan 7.10): getNightOpsModifiers already weights the level, the illumination and the NVG by the share of
  // operations at night (night_ops_percentage), so it is a DAILY AVERAGE modifier. It was applied only when isNight, which Tab 2 never
  // sets, so illumination and NVG did nothing. With a night operations config the average applies; without one the old binary rule.
  const nightMod = nightOpsModifiers
    ? (nightOpsModifiers.medevac_modifier ?? 1.0)
    : (isNight ? NIGHT_MEDEVAC_MODIFIERS.helicopter_availability : 1.0);

  // Terrain modifier - affects sortie rate based on LZ availability
  const terrainKey = terrain?.toUpperCase() || 'MIXED';
  const terrainData = TERRAIN_MODIFIERS?.[terrainKey] || 
                      TERRAIN_MODIFIERS?.ROLLING || 
                      { sortie_modifier: 1.0 };
  const terrainMod = terrainData.sortie_modifier || 1.0;

  // V5-014: HLZ night lighting modifier.
  // SMALL HLZ uses chemical lights only ('Basic night lighting, chemical lights') — no NVG/IR.
  // Reduces availability by 40% when night ops active and HLZ lacks NVG-compatible lighting.
  // MEDIUM/LARGE HLZ ('Full night lighting, NVG compatible, IR markers') → no additional reduction.
  // Only applied when: night ops active AND HLZ is configured AND HLZ lacks NVG lighting.
  const _hlzHasNVGLighting = hlzConfig?.has_nvg_lighting ?? false;
  const hlzNightMod = (isNight && hlzConfig?.configured && !_hlzHasNVGLighting) ? 0.60 : 1.0;

  // Combined availability — includes HLZ night lighting infrastructure constraint (V5-014)
  const combinedAvailability = maintenanceAvail * crewAvail * adMod * weatherMod * nightMod * hlzNightMod;

  // V5-014: HLZ simultaneous capacity cap.
  // An HLZ can only accept N helicopters simultaneously (SMALL=1, MEDIUM=4, LARGE=8).
  // Configuring more aircraft than the HLZ can hold does not increase effective throughput —
  // excess aircraft must orbit or wait, each available landing pad is the bottleneck.
  const _hlzCap = hlzConfig?.cap ?? 999; // 999 = unconstrained (no HLZ configured)
  const availableAircraft = Math.min(
    Math.round(totalAircraft * combinedAvailability),
    _hlzCap
  );

  // V5-014: FARP modifier on sorties/day.
  // Without FARP: helicopters return to main base for fuel, adding ~30-45 min per sortie cycle → -15%.
  // Limited FARP (MEDIUM HLZ): neutral — can refuel 2 aircraft concurrently.
  // Full FARP (LARGE HLZ): forward refuel reduces RTB requirement → +15%.
  // No HLZ configured: 1.0 (no constraint, planning baseline).
  const farpMod = hlzConfig?.farp_mod ?? 1.0;

  // F-04 FIX: envKey now UPPERCASE — reads PERMISSIVE=8, CONTESTED=6, DENIED=3 correctly.
  // Terrain affects sortie rate via LZ availability (round, not floor, for planning purposes).
  const baseSortiesPerAircraft = opFactors.sorties_per_aircraft_per_day?.[envKey] ?? 6;
  const sortiesPerAircraft = Math.round(baseSortiesPerAircraft * terrainMod * farpMod); // V5-014: farpMod
  const dailySorties = availableAircraft * sortiesPerAircraft;

  // F-04 FIX: patients_per_sortie keyed by helicopter code in v2.3 (not .mixed_load).
  // Determine primary helicopter type from the first enabled pool with a known aircraft type.
  let primaryAircraftType = 'DEFAULT';
  if (medevacConfig.pools) {
    for (const pool of Object.values(medevacConfig.pools)) {
      if (pool.enabled && pool.aircraft_count > 0 && pool.aircraft_type) {
        primaryAircraftType = pool.aircraft_type.toUpperCase();
        break;
      }
    }
  }
  const patientsPerSortie = opFactors.patients_per_sortie?.[primaryAircraftType]
    ?? opFactors.patients_per_sortie?.DEFAULT
    ?? 4;
  const dailyPatientCapacityBase = dailySorties * patientsPerSortie;
  // IMP-007: Scale by unit-type helicopter demand factor.
  // AIR_ASSAULT=3.0x → significantly more helicopter throughput modelled.
  // LOGISTICS=0.5x → lower helicopter demand from rear-area unit.
  const dailyPatientCapacity = Math.round(dailyPatientCapacityBase * unitHelicopterFactor);

  // V5-014: HLZ throughput bottleneck check.
  // If daily patient demand exceeds the HLZ's maximum patients/day throughput, the HLZ
  // is the operational bottleneck regardless of aircraft availability.
  const _hlzThroughputPerDay = hlzConfig?.throughput_patients_per_day ?? null;
  const _hlzIsBottleneck = _hlzThroughputPerDay !== null && dailyPatientCapacity > _hlzThroughputPerDay;
  const _hlzCapApplied    = hlzConfig?.configured && hlzConfig.cap < totalAircraft;

  // Limiting factors
  const limitingFactors = [];
  if (adMod < 0.8) limitingFactors.push(`AD threat reducing availability by ${Math.round((1 - adMod) * 100)}%`);
  if (weatherMod < 0.8) limitingFactors.push(`Weather reducing availability by ${Math.round((1 - weatherMod) * 100)}%`);
  if (isNight) limitingFactors.push(`Night operations: ${Math.round((1 - nightMod) * 100)}% reduction`);
  if (maintenanceAvail < 0.8) limitingFactors.push('Maintenance issues limiting availability');
  if (terrainMod < 0.9) limitingFactors.push(`Terrain (${terrain}) limiting LZ availability by ${Math.round((1 - terrainMod) * 100)}%`);
  // V5-014: HLZ-specific limiting factors
  if (!hlzConfig?.configured) limitingFactors.push('No HLZ configured — helicopter MEDEVAC assumes direct POI pickup without designated landing zone');
  if (_hlzCapApplied) limitingFactors.push(`HLZ simultaneous capacity (${hlzConfig.cap}) limits effective aircraft — ${totalAircraft} configured but only ${hlzConfig.cap} can land at once`);
  if (farpMod < 1.0) limitingFactors.push(`No FARP at HLZ — helicopters must RTB for fuel, reducing sorties by ${Math.round((1 - farpMod) * 100)}%`);
  if (hlzNightMod < 1.0) limitingFactors.push(`HLZ lacks NVG lighting — night MEDEVAC limited by HLZ infrastructure (additional ${Math.round((1 - hlzNightMod) * 100)}% reduction)`);
  if (_hlzIsBottleneck) limitingFactors.push(`HLZ throughput bottleneck: HLZ capacity ${_hlzThroughputPerDay} pts/day < calculated demand ${dailyPatientCapacity} pts/day`);

  return {
    total_aircraft: totalAircraft,
    available_aircraft: availableAircraft,
    effective_availability_percent: Math.round(combinedAvailability * 100),

    daily_sorties: dailySorties,
    daily_patient_capacity: dailyPatientCapacity,

    pools: poolBreakdown,

    modifiers: {
      maintenance: maintenanceAvail,
      crew: crewAvail,
      ad_threat: adMod,
      weather: weatherMod,
      night: nightMod,
      hlz_night_lighting: hlzNightMod,  // V5-014
      farp: farpMod,                    // V5-014
      terrain: terrainMod,
      combined: Math.round(combinedAvailability * 100) / 100,
      unit_type_helicopter: unitHelicopterFactor  // IMP-007: unit-type scalar applied
    },

    operational_factors: {
      sorties_per_aircraft: sortiesPerAircraft,
      patients_per_sortie: patientsPerSortie,
      environment: environment
    },

    // V5-014: HLZ capability analysis — consumed by Tab5 MEDEVAC panel
    hlz_analysis: {
      configured:                  hlzConfig?.configured ?? false,
      size:                        hlzConfig?.size ?? null,
      capacity_helicopters:        hlzConfig?.capacity_helicopters ?? null,
      capacity_cap_applied:        _hlzCapApplied,
      has_farp:                    hlzConfig?.has_farp ?? false,
      farp_mod:                    farpMod,
      has_nvg_lighting:            _hlzHasNVGLighting,
      night_lighting_mod:          hlzNightMod,
      throughput_patients_per_day: _hlzThroughputPerDay,
      is_throughput_bottleneck:    _hlzIsBottleneck,
      no_hlz_warning:              !(hlzConfig?.configured ?? false),
    },

    limiting_factors: limitingFactors,

    is_degraded: combinedAvailability < 0.5,
    is_denied: combinedAvailability < 0.1,

    educational_note: generateHelicopterEducationalNote(combinedAvailability, environment, adThreat)
  };
}

/**
 * Generate helicopter educational note
 */
function generateHelicopterEducationalNote(avail, env, adThreat) {
  if (avail < 0.1) {
    return `DENIED: Helicopter MEDEVAC essentially unavailable in ${env} environment with ${adThreat} AD threat. ` +
      'Blue Light Protocol (ground convoy) required.';
  } else if (avail < 0.3) {
    return `SEVERELY DEGRADED: Only ${Math.round(avail * 100)}% helicopter availability. ` +
      'Ground evacuation must be primary method.';
  } else if (avail < 0.6) {
    return `CONTESTED: ${Math.round(avail * 100)}% helicopter availability typical for Ukraine 2022-2024 conditions. ` +
      'Mixed air/ground evacuation recommended.';
  } else {
    return `PERMISSIVE: ${Math.round(avail * 100)}% helicopter availability allows air-primary evacuation.`;
  }
}

// ============================================================================
// GROUND AMBULANCE FUNCTIONS
// ============================================================================

/**
 * Calculate ground ambulance capacity
 * 
 * REPLACES HARDCODED VALUES from v3.1 lines 806-809
 * Uses AMBULANCE_TYPES constants instead of 0.85 × 3 × 2
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Ground ambulance capacity analysis
 */
export function calculateGroundAmbulanceCapacity(config) {
  const {
    ambulanceConfig = {},
    terrain = 'MIXED',
    threatLevel = 'MEDIUM',
    weather = 'CLEAR',
    isNight = false,
    distanceKm = 25,
    unitWheeledFactor = 1.0,   // IMP-007: unit-type wheeled demand scalar
    unitTrackedFactor = 1.0,   // IMP-007: unit-type tracked demand scalar
    nightOpsModifiers = null,  // ENH-032: pre-computed from nightOperations.js via Tab2
    surface = 'PAVED'          // route surface of the R1 to R2B leg (Tab 3 B.3: PAVED, GRAVEL, UNPAVED, TRAIL)
  } = config;

  // Get fleet composition
  const fleet = ambulanceConfig.fleet || ambulanceConfig.vehicles || {};
  
  // Calculate by vehicle type
  const byType = {};
  let totalVehicles = 0;
  let totalLitterCapacity = 0;
  let totalAmbulatoryCapacity = 0;

  // If no fleet specified, use defaults
  if (Object.keys(fleet).length === 0) {
    // Default fleet for a brigade
    fleet.HMMWV_AMBULANCE = 12;
    fleet.TABC_79 = 8;
    fleet.FIELD_AMBULANCE = 4;
  }

  // Get terrain and threat modifiers
  // MED-02/04/05/06/07 FIX: resolvers use canonical constants from terrainFactors.js,
  // operationsConfig.js, and evacuationParadigms.js (medevacModule.js v5.2.0)
  const terrainMod = resolveTerrainModifier(terrain);
  const threatMod = resolveThreatModifier(threatLevel);
  const weatherMod = resolveWeatherModifier(weather).ground;
  // ENH-032: Use level-based nightOpsModifiers.ground_evac_modifier when available.
  // Falls back to binary NIGHT_MEDEVAC_MODIFIERS.ground_speed (0.50) when absent.
  const nightMod = nightOpsModifiers
    ? (nightOpsModifiers.ground_evac_modifier ?? 1.0)
    : (isNight ? NIGHT_MEDEVAC_MODIFIERS.ground_speed : 1.0);

  // Default spec for unknown vehicle types
  const DEFAULT_SPEC = {
    label: 'Unknown Ambulance',
    category: 'WHEELED',
    litter_capacity: 2,
    ambulatory_capacity: 4,
    typical_availability: 0.80,
    trips_per_day: 3,
    speed_km_h: 60,
    terrain_capability: 'ROAD_IMPROVED',
    protection_level: 'NONE',
    crew: 2,
    load_time_minutes: 5,
    unload_time_minutes: 3
  };

  // Calculate capacity for each vehicle type
  for (const [typeName, count] of Object.entries(fleet)) {
    if (!count || count <= 0) continue;

    // Priority: 1) Adapter from ambulanceTypes.js, 2) Local specs, 3) Default
    const spec = getAmbulanceCalcSpec(typeName) || 
                 AMBULANCE_TYPE_SPECS[typeName] || 
                 DEFAULT_SPEC;
    
    // Calculate effective availability
    const baseAvail = spec.typical_availability;
    const terrainAvailMod = terrainMod.availability_modifier;
    const effectiveAvail = baseAvail * terrainAvailMod * weatherMod;
    
    const availableVehicles = Math.round(count * effectiveAvail);

    // Calculate speed modifier
    const speedMod = terrainMod.speed_modifier * threatMod.speed_modifier * nightMod;
    // Ground capacity model (2026-10-10, docs/documentation/SPEC_EVAC_CAPACITY.md): speed by vehicle class and route surface
    // (Dr. Tatar's figures), 10 + 10 + 10 min handling, two 12 h crews per vehicle per day. The old rule took the
    // vehicle's cruise speed and capped runs at the spec's 3 to 4 per day whatever the distance.
    const vehicleClass = vehicleClassFor(spec.category || 'WHEELED');
    // The vehicle cannot exceed its own cruise speed, and its range sets how often it refuels (ambulanceTypes.js).
    const effectiveSpeed = Math.min(groundBaseSpeedKph(vehicleClass, surface), spec.speed_km_h ?? Infinity) * speedMod;
    const roundTripMinutes = groundCycleMinutes({ distanceKm, speedKph: effectiveSpeed });
    const adjustedTrips = groundRunsPerDay({
      distanceKm, vehicleClass, surface, speedModifier: speedMod,
      speedCapKph: spec.speed_km_h ?? Infinity, rangeKm: spec.range_km ?? Infinity
    });

    // Calculate daily capacity
    const dailyLitter = availableVehicles * adjustedTrips * spec.litter_capacity;
    const dailyAmbulatory = availableVehicles * adjustedTrips * spec.ambulatory_capacity;

    byType[typeName] = {
      total_vehicles: count,
      available_vehicles: availableVehicles,
      availability_percent: Math.round(effectiveAvail * 100),
      effective_speed_kmh: Math.round(effectiveSpeed),
      round_trip_minutes: Number.isFinite(roundTripMinutes) ? Math.round(roundTripMinutes) : null,
      trips_per_day: adjustedTrips,
      daily_litter_capacity: dailyLitter,
      daily_ambulatory_capacity: dailyAmbulatory,
      t3_only: spec.t3_only || false
    };

    // IMP-007: Apply unit-type demand scalar per vehicle category.
    // HEAVY brigade (tracked=1.8x) → more throughput from ARMORED/TRACKED vehicles.
    // INFANTRY brigade (wheeled=1.3x) → more throughput from WHEELED vehicles.
    // Category mapping: WHEELED/BUS → unitWheeledFactor; ARMORED/TRACKED → unitTrackedFactor.
    const vehicleCategory = spec.category || 'WHEELED';
    const unitVehicleFactor = (vehicleCategory === 'ARMORED' || vehicleCategory === 'TRACKED')
      ? unitTrackedFactor
      : unitWheeledFactor;
    const scaledLitter     = Math.round(dailyLitter     * unitVehicleFactor);
    const scaledAmbulatory = Math.round(dailyAmbulatory * unitVehicleFactor);
    byType[typeName].daily_litter_capacity     = scaledLitter;
    byType[typeName].daily_ambulatory_capacity = scaledAmbulatory;
    byType[typeName].unit_vehicle_factor        = unitVehicleFactor;  // for Tab5 display

    totalVehicles += count;
    totalLitterCapacity    += scaledLitter;
    totalAmbulatoryCapacity += scaledAmbulatory;
  }

  // Calculate effective combined modifier
  const combinedMod = terrainMod.speed_modifier * threatMod.speed_modifier * weatherMod * nightMod;

  // Calculate average evacuation time
  // Golden Hour check: one-way trip time (POI/R1 to R2B).
  // Formula: distanceKm / (50 km/h planning baseline × combinedMod) × 60 + 10 min load.
  // 50 km/h is a conservative NATO planning baseline for mixed armored/wheeled fleets.
  // This is INTENTIONALLY distinct from the per-vehicle round-trip used in trips_per_day above.
  // The round-trip (line 620) uses vehicle-specific speed for logistics throughput.
  // The one-way (this line) uses a conservative planning speed for safety margin.
  // Caller (calculateComprehensiveMEDEVAC) passes groundDistKm = role2_distance_km - role1_distance_km,
  // which is the actual R1→R2B leg only (corrected by F-03 fix).
  const avgEvacTime = distanceKm / (50 * combinedMod) * 60 + 10;

  // Limiting factors
  const limitingFactors = [];
  if (terrainMod.speed_modifier < 0.7) limitingFactors.push(`Terrain (${terrain}) reducing speed by ${Math.round((1 - terrainMod.speed_modifier) * 100)}%`);
  if (threatMod.speed_modifier < 0.8) limitingFactors.push(`Threat level requiring tactical movement`);
  if (weatherMod < 0.8) limitingFactors.push(`Weather degrading road conditions`);
  if (isNight) limitingFactors.push(`Night operations reducing speed by ${Math.round((1 - nightMod) * 100)}%`);

  return {
    total_vehicles: totalVehicles,
    available_vehicles: Object.values(byType).reduce((sum, t) => sum + t.available_vehicles, 0),
    
    daily_litter_capacity: totalLitterCapacity,
    daily_ambulatory_capacity: totalAmbulatoryCapacity,
    total_daily_capacity: totalLitterCapacity + totalAmbulatoryCapacity,
    
    by_type: byType,
    
    modifiers: {
      terrain: terrainMod.speed_modifier,
      threat: threatMod.speed_modifier,
      weather: weatherMod,
      night: nightMod,
      combined: Math.round(combinedMod * 100) / 100,
      unit_type_wheeled: unitWheeledFactor,  // IMP-007
      unit_type_tracked: unitTrackedFactor   // IMP-007
    },
    
    average_evacuation_minutes: Math.round(avgEvacTime),
    distance_km: distanceKm,
    
    limiting_factors: limitingFactors,
    
    golden_hour_achievable: avgEvacTime <= 60,
    
    educational_note: `Ground evacuation at ${distanceKm}km: ~${Math.round(avgEvacTime)}min. ` +
      `Fleet of ${totalVehicles} vehicles can move ${totalLitterCapacity} litter + ${totalAmbulatoryCapacity} ambulatory per day.`
  };
}

// ============================================================================
// BLUE LIGHT PROTOCOL
// ============================================================================

/**
 * Evaluate Blue Light Protocol activation
 * 
 * Blue Light = Ground convoy evacuation when helicopter unavailable
 * 
 * @param {Object} config - Configuration object
 * @returns {Object} Blue Light Protocol analysis
 */
export function evaluateBlueLightProtocol(config) {
  const {
    helicopterCapacity,
    groundCapacity,
    dailyCasualties = 50,
    bloodStatus = 'GREEN',
    surgicalBacklog = 0
  } = config;

  // Get Blue Light thresholds from constants
  const blThresholds = BLUE_LIGHT_PROTOCOL || {
    helicopter_availability_threshold: 0.20,
    max_runtime_hours: 4,
    cooldown_minutes: 120,
    ground_convoy_size: { min: 4, max: 8 },
    escort_required: true
  };

  // Check activation triggers
  const triggers = [];
  let shouldActivate = false;

  // Helicopter availability trigger
  if (helicopterCapacity.effective_availability_percent < blThresholds.helicopter_availability_threshold * 100) {
    triggers.push('HELICOPTER_UNAVAILABLE');
    shouldActivate = true;
  }

  // AD threat trigger
  if (helicopterCapacity.is_denied) {
    triggers.push('AIRSPACE_DENIED');
    shouldActivate = true;
  }

  // MASCAL trigger
  const totalEvacCapacity = (helicopterCapacity.daily_patient_capacity || 0) + 
                            (groundCapacity.total_daily_capacity || 0);
  if (dailyCasualties > totalEvacCapacity * 0.8) {
    triggers.push('EVACUATION_CAPACITY_EXCEEDED');
    shouldActivate = true;
  }

  // Blood emergency trigger
  if (bloodStatus === 'RED' || bloodStatus === 'BLACK') {
    triggers.push('BLOOD_EMERGENCY');
    shouldActivate = true;
  }

  // MED-10: Surgical backlog threshold from canonical source — single source of truth.
  // MASCAL_TRIGGERS.SURGICAL_BACKLOG.role_2_basic.mascal_critical = 25.
  // NOTE: value was 24 (hardcoded); canonical is 25. Changed from 24 → 25 here.
  // F-05 NOTE (deferred): surgicalBacklog is always 0 in current architecture.
  // medicalConfig.surgical_config?.backlog?.total reads a key that Tab3 does not populate.
  // Tab3 buildUpdates saves surgical_config: {teams, shift_mode, fatigue_level} — no backlog key.
  // The SURGICAL_BACKLOG_CRITICAL trigger is therefore permanently disabled until Tab3 or
  // the casualty calculator begins populating surgical_config.backlog.total.
  // Fix requires Tab3/calculator coordination (deferred, tracked as F-05).
  const surgicalBacklogThreshold = MASCAL_TRIGGERS.SURGICAL_BACKLOG.role_2_basic.mascal_critical;
  if (surgicalBacklog > surgicalBacklogThreshold) {
    triggers.push('SURGICAL_BACKLOG_CRITICAL');
    shouldActivate = true;
  } else if (surgicalBacklog > surgicalBacklogThreshold / 2) {
    // Warning level - backlog building but not yet critical
    triggers.push('SURGICAL_BACKLOG_WARNING');
  }

  // Calculate convoy configuration
  const convoyConfig = calculateBlueLightConvoy(groundCapacity, dailyCasualties);

  return {
    should_activate: shouldActivate,
    triggers,
    trigger_count: triggers.length,
    
    convoy_configuration: convoyConfig,
    
    thresholds: {
      helicopter_availability: blThresholds.helicopter_availability_threshold * 100,
      current_helicopter: helicopterCapacity.effective_availability_percent
    },
    
    capacity_analysis: {
      helicopter_capacity: helicopterCapacity.daily_patient_capacity || 0,
      ground_capacity: groundCapacity.total_daily_capacity || 0,
      total_capacity: totalEvacCapacity,
      daily_casualties: dailyCasualties,
      shortfall: Math.max(0, dailyCasualties - totalEvacCapacity),
      surgical_backlog_hours: surgicalBacklog,
      surgical_backlog_critical: surgicalBacklog > surgicalBacklogThreshold
    },
    
    operational_constraints: {
      max_runtime_hours: blThresholds.max_runtime_hours,
      cooldown_minutes: blThresholds.cooldown_minutes,
      escort_required: blThresholds.escort_required
    },
    
    recommendations: generateBlueLightRecommendations(shouldActivate, triggers, convoyConfig),
    
    educational_note: 'Blue Light Protocol: Ground convoy evacuation when helicopter MEDEVAC unavailable. ' +
      'Requires security escort, limited to 4-hour operations, slower but ensures evacuation continues.'
  };
}

/**
 * Calculate Blue Light convoy configuration
 */
function calculateBlueLightConvoy(groundCapacity, casualties) {
  // MED-09: Read convoy composition ratios from canonical source (EXT-02 delivered these)
  const blConvoyRatios = BLUE_LIGHT_PROTOCOL.convoy_ratios;

  const litter = Math.ceil(casualties * blConvoyRatios.litter_patient_percent);
  const ambulatory = casualties - litter;

  // Calculate vehicles needed
  const litterVehicles = Math.ceil(litter / blConvoyRatios.litter_patients_per_vehicle);
  const ambulatoryVehicles = Math.ceil(ambulatory / blConvoyRatios.ambulatory_patients_per_vehicle);

  // Convoy size — escort_vehicle_ratio = 0.25 means 1 escort per 4 vehicles
  const totalVehicles = litterVehicles + ambulatoryVehicles;
  const escortVehicles = Math.ceil(totalVehicles * blConvoyRatios.escort_vehicle_ratio);

  return {
    ambulances_needed: totalVehicles,
    litter_vehicles: litterVehicles,
    ambulatory_vehicles: ambulatoryVehicles,
    escort_vehicles: escortVehicles,
    total_convoy_size: totalVehicles + escortVehicles,
    capacity: {
      litter: litterVehicles * blConvoyRatios.litter_patients_per_vehicle,
      ambulatory: ambulatoryVehicles * blConvoyRatios.ambulatory_patients_per_vehicle
    },
    feasible: totalVehicles <= (groundCapacity.available_vehicles || 24)
  };
}

/**
 * Generate Blue Light recommendations
 */
function generateBlueLightRecommendations(activate, triggers, convoy) {
  const recs = [];

  if (activate) {
    recs.push('ACTIVATE Blue Light Protocol');
    recs.push(`Prepare convoy of ${convoy.total_convoy_size} vehicles (${convoy.ambulances_needed} ambulances + ${convoy.escort_vehicles} escort)`);
    
    if (triggers.includes('AIRSPACE_DENIED')) {
      recs.push('Airspace denied - ground only evacuation until air defense suppressed');
    }
    
    if (triggers.includes('BLOOD_EMERGENCY')) {
      recs.push('PRIORITY: Blood resupply convoy with returning ambulances');
    }

    if (!convoy.feasible) {
      recs.push('WARNING: Insufficient ambulances for full convoy - prioritize T1 casualties');
    }
  }

  return recs;
}

// ============================================================================
// COMBINED MEDEVAC ANALYSIS
// ============================================================================

/**
 * Calculate comprehensive MEDEVAC availability
 * 
 * Combines helicopter and ground capacity, evaluates Blue Light,
 * determines evacuation method recommendation
 * 
 * @param {Object} config - Complete configuration
 * @returns {Object} Comprehensive MEDEVAC analysis
 */
export function calculateComprehensiveMEDEVAC(config) {
  const {
    medevacConfig = {},
    ambulanceConfig = {},
    scenario = {},
    dailyCasualties = 50,
    medicalConfig = {},
    compositeUnitFactors = null  // IMP-007: strength-weighted unit-type evacuation factors
  } = config;

  // Extract environment parameters
  // v5.5 (Phase 5.1): tactical_evac_config (Tab3 3B.0) takes precedence for AD threat and weather.
  // This ensures user-configured values from the new Tactical Evacuation section drive calculations.
  // Falls back to operation_parameters for backward compatibility with existing saved scenarios.
  const tacEvac     = medicalConfig?.tactical_evac_config;
  const environment = resolveEvacuationParadigm(scenario).paradigm;
  const adThreatRaw = tacEvac?.ad_threat  || scenario.operation_parameters?.ad_threat  || 'MEDIUM';
  const weather     = tacEvac?.weather    || scenario.operation_parameters?.weather    || 'VMC_CLEAR';
  const terrain     = resolveScenarioEnvironment(scenario).terrain;   // CALC-011: planner's terrain (Tab 2)
  const isNight     = scenario.operation_parameters?.time_of_day === 'NIGHT';

  // ENH-032: Extract nightOpsModifiers from scenario (pre-computed by Tab2 via getNightOpsModifiers()).
  // When enabled, provides level-based medevac_modifier + ground_evac_modifier (NIGHT_OPS_LEVELS ×
  // ILLUMINATION_CONDITIONS × NVG_AVAILABILITY). Passed to capacity functions to replace binary nightMod.
  const _nightOpsCfg    = scenario.operation_parameters?.night_ops_config;
  const _nightOpsModifiers = (_nightOpsCfg?.enabled && _nightOpsCfg?.modifiers)
    ? _nightOpsCfg.modifiers
    : null;

  // ENH-016: Apply friendly air situation AD threat override.
  // AIR_SUPERIORITY → PERMISSIVE (helicopters unrestricted).
  // CONTESTED       → HIGH (upgrade regardless of Tab3 setting).
  // ENEMY_AIR_SUPERIORITY → DENIED (helicopters cannot operate).
  // PARITY          → no override (null), use Tab3 ad_threat as configured.
  // The override is applied on top of adThreatRaw — only if non-null.
  const _airSitLevel   = scenario.operation_parameters?.air_situation_config?.level ?? 'PARITY';
  const _adOverride    = AIR_SITUATION_LEVELS[_airSitLevel]?.medevac_ad_threat_override ?? null;
  const adThreat       = _adOverride !== null ? _adOverride : adThreatRaw;
  // 2026-10-13 (Tab 2 plan 7.9): the helicopter availability model (air situation ceiling x enemy air defense x operation weather)
  const _threatAssessment = scenario.operation_parameters?.threat_assessment;
  const _airModel = (THREAT_EFFECTS_USE && _threatAssessment?.enemy_ad)
    ? helicopterAvailability({ airSituationLevel: _airSitLevel, enemyAd: _threatAssessment.enemy_ad, weather: resolveScenarioEnvironment(scenario).weather, coordination: infrastructureModel(scenario.operation_parameters?.infrastructure_config)?.medevac_coordination ?? 1 })
    : null;

  // F-01 FIX: Read correct Tab3 field names (role2_distance_km not role2).
  // Tab3 buildUpdates saves: medical_configuration.mtf_distances.{role1,role2,role3,role4}_distance_km.
  // Previous code read mtf_distances?.role2 — that key does not exist. || 25 fallback always fired.
  // APP_RULES 6.3: throw with context when required data is absent.
  const mtfDist = medicalConfig?.mtf_distances;
  // BUG-NEW-001 FIX: Use NATO doctrine defaults when mtf_distances absent (e.g. default scenario load
  // before Tab 3 save). R2B default 25 km, R1 default 8 km per AJMedP-1 planning norms.
  // Previously threw here — broke calculator when loading any default scenario.

  // F-03 FIX: Helicopter and ground ambulance use different distances.
  // Helicopter: from POI (at/near FEBA) to R2B = role2_distance_km (full FEBA→R2B distance).
  //   Source: NATO AJMedP-2: helicopter MEDEVAC picks up at point of injury, not R1.
  // Ground ambulance: from R1 to R2B = role2_distance_km - role1_distance_km (R1→R2B leg only).
  //   R1 facility is already at role1_distance_km from FEBA — ambulances start there.
  //   Previous code: passed same distanceKm to both → overstated ground trip by role1_distance_km.
  const r1DistKm   = mtfDist?.role1_distance_km ?? 8;   // R1 position from FEBA (default 8 km AJMedP-1)
  const r2DistKm   = mtfDist?.role2_distance_km ?? 25;  // R2B position from FEBA (default 25 km AJMedP-1)
  const heliDistKm   = r2DistKm;                         // helicopter: POI → R2B
  const groundDistKm = Math.max(1, r2DistKm - r1DistKm); // ambulance: R1 → R2B (minimum 1 km)

  // V5-014: Extract HLZ configuration from medicalConfig.
  // HLZ capability (size, FARP, night lighting) modifies helicopter availability and sortie rate.
  // helicopter_facilities[] is saved by Tab3 buildUpdates(); primary HLZ is index 0.
  // When no HLZ is configured, hlzConfig.configured = false and all modifiers are 1.0 (no change).
  const _hlzFacilities = medicalConfig?.helicopter_facilities || [];
  const _primaryHLZ    = _hlzFacilities.length > 0 ? _hlzFacilities[0] : null;
  const _hlzRefueling  = _primaryHLZ?.equipment?.refueling || '';
  const _hasFARPLocal  = _primaryHLZ !== null && !_hlzRefueling.toLowerCase().includes('none');
  const _hlzLighting   = _primaryHLZ?.equipment?.lighting || '';
  const _hlzHasNVGLocal = _hlzLighting.toLowerCase().includes('nvg');
  // FARP modifier: no FARP = 0.85, limited FARP (MEDIUM) = 1.0, full FARP (LARGE) = 1.15
  const _hlzFarpMod = !_primaryHLZ ? 1.0
    : _hasFARPLocal ? (_hlzRefueling.includes('Full') ? 1.15 : 1.0)
    : 0.85;
  const hlzConfig = {
    configured:                  _primaryHLZ !== null,
    size:                        _primaryHLZ?.facility_size ?? null,
    capacity_helicopters:        _primaryHLZ?.capacity_helicopters ?? null,
    has_farp:                    _hasFARPLocal,
    has_nvg_lighting:            _hlzHasNVGLocal,
    farp_mod:                    _hlzFarpMod,
    // night_lighting_mod computed inside calculateHelicopterCapacity (needs isNight)
    cap:                         _primaryHLZ?.capacity_helicopters ?? 999,
    throughput_patients_per_day: _primaryHLZ
      ? (_primaryHLZ.throughput?.patients_per_hour ?? 8) * 12
      : null,
  };

  // Calculate helicopter capacity (distance used for sortie planning at this stage is
  // handled internally via MEDEVAC_OPERATIONAL_FACTORS.evac_planning_mins_per_km below)
  const helicopterCapacity = calculateHelicopterCapacity({
    medevacConfig,
    environment,
    adThreat,
    weather,
    isNight,
    terrain,
    unitHelicopterFactor: compositeUnitFactors?.helicopter_factor ?? 1.0,  // IMP-007
    nightOpsModifiers: _nightOpsModifiers,  // ENH-032
    hlzConfig,                              // V5-014: HLZ capability modifiers
    airModel: _airModel,                    // 2026-10-13: one helicopter availability (null without a threat assessment)
  });

  // Calculate ground capacity — pass groundDistKm (R1→R2B leg only, corrected by F-03)
  const groundCapacity = calculateGroundAmbulanceCapacity({
    ambulanceConfig,
    terrain,
    threatLevel: adThreat,
    weather,
    isNight,
    distanceKm: groundDistKm,  // F-03 FIX: R1→R2B only, not full FEBA→R2B
    unitWheeledFactor: compositeUnitFactors?.wheeled_factor    ?? 1.0,  // IMP-007
    unitTrackedFactor: compositeUnitFactors?.tracked_factor    ?? 1.0,  // IMP-007
    nightOpsModifiers: _nightOpsModifiers,  // ENH-032
    // Surface of the R1 to R2B leg as the planner set it in Tab 3 B.3 (PAVED when not set; listed in the assumptions)
    surface: resolveInput(scenario?.medical_configuration?.evacuation_routes?.r1_to_r2b?.primary?.surface, 'evac_route_surface')
  });

  // ENH-048a: Monte Carlo medevac_reliability modifier.
  // medevac_reliability > 1.0 = more reliable fleet = higher effective capacity.
  // medevac_reliability < 1.0 = degraded reliability (maintenance backlog, attrition) = lower capacity.
  // Applied to both air and ground capacity results before Blue Light and total capacity calculations.
  // No-op at 1.0 — deterministic runs never set monte_carlo_modifiers. Math.max(0) guards.
  const _mcMedevacMod = scenario?.operation_parameters?.monte_carlo_modifiers?.medevac_reliability ?? 1.0;
  if (_mcMedevacMod !== 1.0) {
    helicopterCapacity.available_aircraft     = Math.max(0, Math.round(helicopterCapacity.available_aircraft     * _mcMedevacMod));
    helicopterCapacity.daily_sorties          = Math.max(0, Math.round(helicopterCapacity.daily_sorties          * _mcMedevacMod));
    helicopterCapacity.daily_patient_capacity = Math.max(0, Math.round(helicopterCapacity.daily_patient_capacity * _mcMedevacMod));
    groundCapacity.available_vehicles         = Math.max(0, Math.round(groundCapacity.available_vehicles         * _mcMedevacMod));
    groundCapacity.total_daily_capacity       = Math.max(0, Math.round(groundCapacity.total_daily_capacity       * _mcMedevacMod));
  }

  // V4-002: terrain_difficulty — longer transit per sortie = fewer sorties completed per day.
  // Applied as divisor on daily_patient_capacity (harder terrain = fewer sorties fit in 24h).
  // terrain_difficulty > 1.0 = harder = longer per-sortie time = lower daily throughput.
  const _mcTerrainMod = scenario?.operation_parameters?.monte_carlo_modifiers?.terrain_difficulty ?? 1.0;
  if (_mcTerrainMod !== 1.0) {
    helicopterCapacity.daily_sorties          = Math.max(0, Math.round(helicopterCapacity.daily_sorties          / _mcTerrainMod));
    helicopterCapacity.daily_patient_capacity = Math.max(0, Math.round(helicopterCapacity.daily_patient_capacity / _mcTerrainMod));
    groundCapacity.total_daily_capacity       = Math.max(0, Math.round(groundCapacity.total_daily_capacity       / _mcTerrainMod));
  }

  // V4-002: equipment_readiness — mechanical state of fleet and ground vehicles (maintenance deferred,
  // spare parts shortage). Distinct from medevac_reliability (operational availability = mission abort
  // rate). equipment_readiness < 1.0 = fewer sorties completed due to unscheduled maintenance.
  const _mcEquipMod = scenario?.operation_parameters?.monte_carlo_modifiers?.equipment_readiness ?? 1.0;
  if (_mcEquipMod !== 1.0) {
    helicopterCapacity.daily_sorties          = Math.max(0, Math.round(helicopterCapacity.daily_sorties          * _mcEquipMod));
    helicopterCapacity.daily_patient_capacity = Math.max(0, Math.round(helicopterCapacity.daily_patient_capacity * _mcEquipMod));
    groundCapacity.total_daily_capacity       = Math.max(0, Math.round(groundCapacity.total_daily_capacity       * _mcEquipMod));
  }

  // V4-002: communication_effectiveness — C2 coordination efficiency. Poor comms = dispatch delays,
  // misrouting, hand-off overhead = fewer sorties dispatched and completed per day.
  // communication_effectiveness < 1.0 = degraded C2 = lower effective sortie rate.
  const _mcCommsMod = scenario?.operation_parameters?.monte_carlo_modifiers?.communication_effectiveness ?? 1.0;
  if (_mcCommsMod !== 1.0) {
    helicopterCapacity.daily_sorties          = Math.max(0, Math.round(helicopterCapacity.daily_sorties          * _mcCommsMod));
    helicopterCapacity.daily_patient_capacity = Math.max(0, Math.round(helicopterCapacity.daily_patient_capacity * _mcCommsMod));
    groundCapacity.total_daily_capacity       = Math.max(0, Math.round(groundCapacity.total_daily_capacity       * _mcCommsMod));
  }

  // Evaluate Blue Light Protocol
  const blueLightAnalysis = evaluateBlueLightProtocol({
    helicopterCapacity,
    groundCapacity,
    dailyCasualties,
    bloodStatus: medicalConfig.blood_config?.alert?.current_level || 'GREEN',
    surgicalBacklog: medicalConfig.surgical_config?.backlog?.total || 0
  });

  // Total evacuation capacity
  const totalCapacity = helicopterCapacity.daily_patient_capacity + groundCapacity.total_daily_capacity;
  const shortfall = Math.max(0, dailyCasualties - totalCapacity);

  // Determine recommended method
  let recommendedMethod = 'MIXED';
  if (helicopterCapacity.is_denied || helicopterCapacity.effective_availability_percent < 20) {
    recommendedMethod = 'GROUND_PRIMARY';
  } else if (helicopterCapacity.effective_availability_percent > 70) {
    recommendedMethod = 'AIR_PRIMARY';
  }

  // F-06 FIX: NTM lookup — determine primary helicopter type from medevacConfig pools.
  // NTM (Notice To Move) = time from alert to wheels-up. Consumes part of the 60-minute
  // Golden Hour budget because the GH clock starts at wounding, not at helicopter launch.
  // Source: PECC doctrine (settled fact in CLAUDE_READING_LIST):
  //   IAR330=10 min / UH60=15 min / CH47=30 min.
  // All values in MEDEVAC_OPERATIONAL_FACTORS.ntm_minutes (helicopterSpecs.js v2.3).
  let ntmAircraftType = 'DEFAULT';
  if (medevacConfig.pools) {
    for (const pool of Object.values(medevacConfig.pools)) {
      if (pool.enabled && pool.aircraft_count > 0 && pool.aircraft_type) {
        ntmAircraftType = pool.aircraft_type.toUpperCase();
        break;
      }
    }
  }
  const ntmMinutes = MEDEVAC_OPERATIONAL_FACTORS.ntm_minutes?.[ntmAircraftType]
    ?? MEDEVAC_OPERATIONAL_FACTORS.ntm_minutes?.DEFAULT
    ?? 15; // NATO planning default — should never fire with v2.3 constant

  // MED-11 FIX: Literals replaced with named constants from MEDEVAC_OPERATIONAL_FACTORS.
  // F-03 FIX: Use heliDistKm (full POI→R2B) for helicopter; groundEvacTime already
  // computed by calculateGroundAmbulanceCapacity using groundDistKm (R1→R2B only).
  // F-06 FIX: Add NTM to heliEvacTime.
  //   GH clock starts at wounding. NTM elapses before helicopter lifts off.
  //   Full time from wounding to patient delivery at R2B:
  //   NTM + (one-way distance × 2 min/km) + load/unload (15 min).
  const heliEvacTime = ntmMinutes
                     + heliDistKm * MEDEVAC_OPERATIONAL_FACTORS.evac_planning_mins_per_km
                     + MEDEVAC_OPERATIONAL_FACTORS.evac_planning_load_minutes;
  const groundEvacTime = groundCapacity.average_evacuation_minutes;

  // F-06: Max range at which GH is still achievable (range ring):
  //   60 min budget − NTM − load_minutes = remaining for one-way flight
  //   ghRangeRingKm = remaining / evac_planning_mins_per_km
  const ghBudgetAfterNtm = 60 - ntmMinutes - MEDEVAC_OPERATIONAL_FACTORS.evac_planning_load_minutes;
  const ghRangeRingKm = Math.max(0, ghBudgetAfterNtm / MEDEVAC_OPERATIONAL_FACTORS.evac_planning_mins_per_km);

  // Calculate blended golden hour achievement
  const heliGHAchievement = heliEvacTime <= 60 ? 1.0 : Math.max(0, 1 - (heliEvacTime - 60) / 60);
  const groundGHAchievement = groundEvacTime <= 60 ? 1.0 : Math.max(0, 1 - (groundEvacTime - 60) / 60);
  
  const heliRatio = helicopterCapacity.daily_patient_capacity / Math.max(1, totalCapacity);
  const blendedGHAchievement = (heliGHAchievement * heliRatio) + (groundGHAchievement * (1 - heliRatio));

  return {
    helicopter: helicopterCapacity,
    ground: groundCapacity,
    blue_light: blueLightAnalysis,
    
    total_daily_capacity: totalCapacity,
    expected_casualties: dailyCasualties,
    capacity_surplus_deficit: totalCapacity - dailyCasualties,
    capacity_utilization_percent: Math.round((dailyCasualties / Math.max(1, totalCapacity)) * 100),
    
    recommended_method: recommendedMethod,
    
    golden_hour_analysis: {
      helicopter_evac_minutes: heliEvacTime,
      ground_evac_minutes: groundEvacTime,
      helicopter_gh_achievable: heliEvacTime <= 60,
      ground_gh_achievable: groundEvacTime <= 60,
      blended_gh_achievement_percent: Math.round(blendedGHAchievement * 100),
      // F-06: NTM-corrected range ring
      ntm_minutes_used: ntmMinutes,           // NTM for primary aircraft type
      ntm_aircraft_type: ntmAircraftType,     // Aircraft type used for NTM lookup
      gh_range_ring_km: Math.round(ghRangeRingKm * 10) / 10, // Max range for GH (one decimal)
      gh_budget_after_ntm: ghBudgetAfterNtm,  // Remaining GH budget after NTM (min)
      // F-01/F-03: Expose distances used so Tab5 can display what was actually calculated
      heli_distance_km: heliDistKm,     // helicopter: POI → R2B (role2_distance_km)
      ground_distance_km: groundDistKm, // ambulance: R1 → R2B (role2 - role1)
      r1_distance_km: r1DistKm,
      r2_distance_km: r2DistKm
    },
    
    shortfall,
    is_adequate: shortfall === 0,
    is_critical: shortfall > dailyCasualties * 0.2,
    
    recommendations: [
      ...generateMEDEVACRecommendations(recommendedMethod, shortfall, helicopterCapacity, groundCapacity),
      ...blueLightAnalysis.recommendations
    ],
    
    educational_insight: generateMEDEVACEducationalInsight(
      recommendedMethod, 
      helicopterCapacity, 
      groundCapacity, 
      blendedGHAchievement
    )
  };
}

/**
 * Generate MEDEVAC recommendations
 */
function generateMEDEVACRecommendations(method, shortfall, heli, ground) {
  const recs = [];

  if (method === 'GROUND_PRIMARY') {
    recs.push('Ground evacuation primary due to limited helicopter availability');
    recs.push('Position Role 2 forward to achieve golden hour by ground');
  }

  if (shortfall > 0) {
    recs.push(`WARNING: ${shortfall} casualty evacuation shortfall expected`);
    recs.push('Request additional MEDEVAC assets or reduce MTF distance');
  }

  if (heli.is_degraded && !heli.is_denied) {
    recs.push('Prioritize helicopter for T1 casualties only');
    recs.push('Use ground for T2/T3 evacuation');
  }

  return recs;
}

/**
 * Generate MEDEVAC educational insight
 */
function generateMEDEVACEducationalInsight(method, heli, ground, ghAchieve) {
  let insight = `Recommended evacuation: ${method}. `;
  
  insight += `Helicopter: ${heli.available_aircraft} of ${heli.total_aircraft} available (${heli.effective_availability_percent}%). `;
  insight += `Ground: ${ground.available_vehicles} ambulances, ${ground.total_daily_capacity} capacity/day. `;
  insight += `Golden hour achievement: ${Math.round(ghAchieve * 100)}%. `;

  if (method === 'GROUND_PRIMARY') {
    insight += 'Ukraine 2022-2024 showed 42% helicopter availability in contested environment - ground evacuation skills critical.';
  }

  return insight;
}

// ============================================================================
// ENH-002: getEffectiveAssets(facility, day)
// Returns the net asset counts for a facility on a specific operation day,
// accounting for attached assets (added) and detached assets (subtracted).
//
// Called by casualtyCalculator.js per-day loop when building tactical evac
// capacity, and by Tab5 renderAttachmentTimeline() for reporting.
//
// Data shapes (from Tab3 ENH-002 / MedicalService v2.1.0):
//   facility.attached_assets[]: { asset_type, model, quantity,
//                                  attach_day, detach_day (null=end) }
//   facility.detached_assets[]: { asset_type, quantity,
//                                  detach_day, return_day (null=permanent) }
//
// @param {Object} facility - facility object from forceBuilderFacilities /
//                            per_facility_evacuation_assets
// @param {number} day      - 1-based operation day
// @returns {Object}        - { helicopters, ambulances, delta_heli, delta_amb,
//                              attachments_active, detachments_active }
// ============================================================================
export function getEffectiveAssets(facility, day) {
  const organicHeli = facility?.organic_assets?.helicopters?.allocated
                   ?? facility?.organic_assets?.medevac_helicopters
                   ?? 0;
  const organicAmb  = (facility?.organic_assets?.ground_ambulances?.wheeled?.count  || 0)
                    + (facility?.organic_assets?.ground_ambulances?.armored?.count   || 0)
                    + (facility?.organic_assets?.ambulances_wheeled                  || 0)
                    + (facility?.organic_assets?.ambulances_armored                  || 0);

  const attached  = Array.isArray(facility?.attached_assets)  ? facility.attached_assets  : [];
  const detached  = Array.isArray(facility?.detached_assets)  ? facility.detached_assets  : [];

  // Attachments active on this day: attach_day <= day <= (detach_day ?? Infinity)
  const activeAttachments = attached.filter(a => {
    const start = a.attach_day ?? 1;
    const end   = a.detach_day != null ? a.detach_day : Infinity;
    return day >= start && day <= end;
  });

  // Detachments active on this day: detach_day <= day <= (return_day ?? Infinity)
  const activeDetachments = detached.filter(d => {
    const start = d.detach_day ?? 1;
    const end   = d.return_day != null ? d.return_day : Infinity;
    return day >= start && day <= end;
  });

  let deltaHeli = 0;
  let deltaAmb  = 0;

  activeAttachments.forEach(a => {
    const qty = a.quantity ?? 0;
    if (a.asset_type === 'HELICOPTER') deltaHeli += qty;
    else                               deltaAmb  += qty;
  });

  activeDetachments.forEach(d => {
    const qty = d.quantity ?? 0;
    if (d.asset_type === 'HELICOPTER') deltaHeli -= qty;
    else                               deltaAmb  -= qty;
  });

  return {
    helicopters:          Math.max(0, organicHeli + deltaHeli),
    ambulances:           Math.max(0, organicAmb  + deltaAmb),
    organic_helicopters:  organicHeli,
    organic_ambulances:   organicAmb,
    delta_heli:           deltaHeli,
    delta_amb:            deltaAmb,
    attachments_active:   activeAttachments,
    detachments_active:   activeDetachments
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  AMBULANCE_TYPE_SPECS,
  TERRAIN_TYPES_TF,        // replaces deleted TERRAIN_GROUND_MODIFIERS (MED-02/04)
  THREAT_LEVELS,           // replaces deleted THREAT_GROUND_MODIFIERS (MED-05)
  WEATHER_EVACUATION_MODIFIERS, // replaces deleted WEATHER_MODIFIERS (MED-01/06)
  NIGHT_MEDEVAC_MODIFIERS
};

// Named default export (fixes lint warning)
const medevacModule = {
  // Main calculation functions
  calculateHelicopterCapacity,
  calculateGroundAmbulanceCapacity,
  evaluateBlueLightProtocol,
  calculateComprehensiveMEDEVAC,
  
  // Local constants (exported for testing/reference)
  AMBULANCE_TYPE_SPECS,
  TERRAIN_TYPES_TF,            // replaces deleted TERRAIN_GROUND_MODIFIERS (MED-02/04)
  THREAT_LEVELS,               // replaces deleted THREAT_GROUND_MODIFIERS (MED-05)
  WEATHER_EVACUATION_MODIFIERS, // replaces deleted WEATHER_MODIFIERS (MED-01/06)
  NIGHT_MEDEVAC_MODIFIERS
};

export default medevacModule;
